import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../hooks";
import { AppRoutes, ButtonsType, CampaignPurpose } from "../../types/enums";
import { SettingsIcon } from "../images";
import { CustomizeTargetingBtn, ManageSectionsBtn } from "./buttons";
import { DropdownWithQuery } from "./dropdown/DropdownWithQuery";
import { selectPermissions } from "../../features/permissionsSlice";
import { ReuseButton } from "./ReuseButton";

export const ReportsTabs = ({
  isNeedUpdate,
  isDisabled,
  isNoAccounts,
  campaignPurpose,
}: {
  isNeedUpdate: boolean;
  isDisabled: boolean;
  isNoAccounts: boolean;
  campaignPurpose?: CampaignPurpose;
}) => {
  const navigate = useNavigate();
  const { campaign_id, tab } = useParams();
  const { isAllowedEditGenerateReports, isAllowedEditResyncData } =
    useAppSelector(selectPermissions);

  const isSampleCampaign =
    campaignPurpose === CampaignPurpose.demo ||
    campaignPurpose === CampaignPurpose.sales;

  const tabList = [
    {
      label: "Targeted Accounts",
      path: "list",
      disabled: false,
    },
    {
      label: "Recommended Accounts",
      path: "recommended",
      disabled: !isAllowedEditResyncData || isNoAccounts,
    },
    {
      label: "Reports",
      path: "reports",
      disabled: !isAllowedEditGenerateReports,
    },
  ];

  const tabIndex = tabList.findIndex(({ path }) => path === tab);

  const handleChange = (e: React.SyntheticEvent, idx: number) => {
    navigate(`${AppRoutes.ACCOUNTS}/${tabList[idx].path}/${campaign_id}`);
  };

  return (
    <Stack flexDirection="row" gap={"24px"}>
      <Box
        sx={{
          display: "flex",
          borderBottom: "1px solid #D9D9D9",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexGrow: 1,
        }}
      >
        <Tabs
          value={tabIndex === -1 ? 0 : tabIndex}
          onChange={handleChange}
          sx={{
            borderBottom: "none",
            minHeight: "unset",
          }}
        >
          {tabList.map(({ label, disabled }, index) => (
            <Tab
              key={label}
              label={label}
              disabled={
                (tabList.length - 1 === index && isDisabled) || disabled
              }
              sx={{
                minHeight: "unset",
                width: "fit-content",
                textWrap: "nowrap",
                padding: "4px 16px",
                fontSize: "14px",
                lineHeight: "26px",
                fontWeight: 500,
                "&.Mui-selected": { color: "#1C3C6C" },
                ...(tabList.length - 1 === index &&
                  isNeedUpdate && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 14,
                      right: 10,
                      width: "6px",
                      height: "6px",
                      backgroundColor: "#FD8430",
                      borderRadius: "50%",
                    },
                  }),
              }}
            />
          ))}
        </Tabs>

        {tabIndex === 1 && <CustomizeTargetingBtn campaign_id={campaign_id!} />}
        {tabIndex === 2 && (
          <ManageSectionsBtn
            campaign_id={campaign_id!}
            customButton={(onClick) => (
              <ReuseButton
                onClick={onClick}
                startIcon={<SettingsIcon />}
                disabled={!isAllowedEditGenerateReports}
                buttonType={ButtonsType.text}
              >
                Manage Sections
              </ReuseButton>
            )}
          />
        )}
      </Box>
      {!tabIndex && !isSampleCampaign && (
        <Stack flexDirection="row" gap="12px" alignItems="center">
          <Typography variant="body1" color="#585858">
            Trend Time Range:
          </Typography>
          <DropdownWithQuery
            query={"period"}
            label={""}
            items={["Last Week", "Last Month", "Last Quarter "]}
            placeholder={""}
            initSelected={"Last Month"}
            maxHeight="134px"
          />
        </Stack>
      )}
    </Stack>
  );
};
